import { Box, SxProps, Typography, useTheme } from '@mui/material';
import { PreviewCard } from 'components/UI/PreviewCard';
import { IProduct } from '@repo/types';
import { tilePx, tileRadius } from 'settings/brandRadius';

export const ProductCard = ({
  product,
  sx,
  bare = false,
}: {
  product: Omit<IProduct, 'company_id'>;
  sx?: SxProps;
  /**
   * Render the row WITHOUT the bordered PreviewCard container.
   *
   * For the one case where this row is read-only context rather than a control —
   * the Gift sheet's "Item", which names the certificate being gifted and cannot
   * be changed. A border there claims an affordance the row does not have, and it
   * made the Item and Recipient rows look like two of the same thing when only
   * Recipient is a field. Defaults to false, so every existing call site is
   * unchanged.
   */
  bare?: boolean;
}) => {
  const theme = useTheme();
  const content = (
    // VERA-304: the container (border / radius / padding / row layout) now comes
    // from the shared PreviewCard, so this card and the Recipient card beside it in
    // the Gift sheet cannot drift apart again — they had different corner radii
    // because each owned its own copy of these values.
    <>
      <Box
        sx={theme => ({
          width: 48,
          height: 48,
          border: `1px solid ${theme.palette.grey[300]}`,
          // VRG-562: the tenant's Corner radius, clamped to the tile's own 48px so a
          // brand at the top of the range gets a rounded square rather than a blob.
          // The `img` below it resolves the same value.
          borderRadius: tileRadius(48),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        })}
      >
        {product.images && product.images.length > 0 && (
          <img
            src={product.images[0] ?? ''}
            alt={product.title}
            style={{
              width: '48px',
              height: '48px',
              objectFit: 'cover',
              // VRG-562: the same clamped value the tile above resolves to.
              borderRadius: `${tilePx(theme.shape.borderRadius, 48)}px`,
            }}
          />
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        {/* VERA-315: `body1`, not `h6`. This is CONTENT — the product's name — so
            it stays at 16/500; what changed is where the 16 comes from. `h6` is
            not declared in this app's type scale (settings/mui/typography.ts
            stops at h5), so it fell through to MUI's default 20px and was then
            pulled back to 16 inline — the variant was being borrowed purely as a
            fontWeight-500 carrier, and it dragged MUI's 1.6 leading along with it.
            `body1` IS this app's 16px step, so the size and leading now come from
            the scale and only the weight is stated. */}
        <Typography variant='body1' sx={{ fontWeight: 500, marginBottom: 0.5 }}>
          {product.title}
        </Typography>
        <Typography variant='body2' sx={theme => ({ color: theme.palette.text.secondary })}>
          {product.sku}
        </Typography>
      </Box>
    </>
  );

  return bare ? (
    // Same row layout PreviewCard supplies (centred, 16px gap), minus the border,
    // radius and padding — so bare and boxed stay visually identical apart from
    // the container itself.
    <Box sx={[{ display: 'flex', alignItems: 'center', gap: 2 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {content}
    </Box>
  ) : (
    <PreviewCard sx={sx}>{content}</PreviewCard>
  );
};
